/**
 * Hook: usePostHogIdentify
 *
 * Attaches the anonymous PostHog session to the Clerk user once they sign in,
 * and clears it on sign-out.
 *
 * WHY THIS MATTERS:
 * The Scratch & Win flow lets people spin the wheel *before* authenticating —
 * the spin is recorded against an anonymous distinct ID held in localStorage.
 * The sign-in gate then sits between the prize and its delivery. Without an
 * explicit identify() call, the pre-auth spin and the post-auth claim are two
 * separate people, and the funnel reports near-zero conversion at exactly the
 * step we most need to measure. identify() merges them.
 *
 * Usage:
 *   Add once at the app root, alongside useEnsureConvexUser():
 *   usePostHogIdentify();
 *
 * PII: identifies by Clerk user ID only. No email, name, or phone — see the
 * logging rules in `@/lib/logger`.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { capture, identifyUser, resetIdentity, EVENTS } from '@/lib/posthog';

/**
 * Where the "we already counted this sign-in" mark lives.
 *
 * A `useRef` survives re-renders but NOT a page load, and the hook remounts on
 * every full load. So a signed-in visitor who reloaded, opened a link in a new
 * tab, or came back from Amazon re-fired `signin_completed` without ever
 * signing in — funnel step 4 inflated with pageviews rather than counting
 * sign-ins, and it feeds the step-conversion math on the admin dashboard.
 *
 * Measured in production 2026-08-23: the same distinct_id fired the event at
 * 02:13:52, 02:25:12 and 02:39:51 in one 26-minute session with no sign-in in
 * between. Found by the `attribution` session during a live test walk.
 *
 * sessionStorage rather than localStorage on purpose: one count per browsing
 * session is the honest reading of "signed in", and a genuine sign-in in a
 * fresh session should still count.
 */
const SIGNIN_COUNTED_KEY = 'iv_signin_counted_for';

function alreadyCounted(userId: string): boolean {
  try {
    return window.sessionStorage.getItem(SIGNIN_COUNTED_KEY) === userId;
  } catch {
    // Storage blocked. Fall back to counting — an over-count is a worse
    // number than a missing one, but a SILENT over-count is worse still, and
    // the ref guard below still suppresses the per-render case.
    return false;
  }
}

function markCounted(userId: string): void {
  try {
    window.sessionStorage.setItem(SIGNIN_COUNTED_KEY, userId);
  } catch {
    // Nothing to persist to; the ref still guards this page load.
  }
}

export function usePostHogIdentify(): void {
  const { isSignedIn, isLoaded, userId } = useAuth();
  // Tracks who we last identified, so a re-render does not re-fire signin_completed.
  const identifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn && userId) {
      if (identifiedUserId.current === userId) return;

      // identify() is idempotent and must run on every load so the session is
      // attached to the person — only the EVENT is deduplicated.
      identifyUser(userId);
      identifiedUserId.current = userId;

      if (!alreadyCounted(userId)) {
        // Funnel step 4. Once per browsing session, not once per page load.
        capture(EVENTS.SIGNIN_COMPLETED);
        markCounted(userId);
      }
      return;
    }

    // Signed out: drop the identity so the next visitor on this device is not
    // attributed to the previous user. Only reset if we had actually identified.
    if (!isSignedIn && identifiedUserId.current !== null) {
      resetIdentity();
      identifiedUserId.current = null;
      // Clear the session mark too, so a genuine sign-in AFTER a sign-out is
      // counted. Without this, signing out and back in within one session
      // would be silently uncounted — trading an over-count for an under-count.
      try {
        window.sessionStorage.removeItem(SIGNIN_COUNTED_KEY);
      } catch {
        // Nothing persisted; nothing to clear.
      }
    }
  }, [isLoaded, isSignedIn, userId]);
}
